import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PagesService } from '../../pages/pages.service';
import { B2BUnit, AccountType, AccountListResponse } from '../../pages/pages.models';

@Component({
  selector: 'app-jjsv-account-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './jjsv-account-select.component.html',
  styleUrl: './jjsv-account-select.component.scss',
})
export class JjsvAccountSelectComponent implements OnChanges {
  @Input() cartId = '';
  @Input() accountType: AccountType = 'SHIP_TO';
  /** List already returned by a previous call; when set, skips the initial getB2bAccounts request. */
  @Input() initialData: AccountListResponse | null = null;
  /** Selected Ship-To uid/name used as search context for the Sold-To list (updateAccountForCurrentCustomer). */
  @Input() contextAccountUid = '';
  @Input() contextAccountName = '';
  @Output() accountSelected = new EventEmitter<B2BUnit>();
  @Output() closed = new EventEmitter<void>();

  private readonly pagesService = inject(PagesService);

  readonly accounts = signal<B2BUnit[]>([]);
  readonly isLoading = signal(false);
  readonly currentPage = signal(0);
  readonly totalPages = signal(0);
  readonly totalResults = signal(0);
  readonly hasNext = signal(false);
  readonly hasPrevious = signal(false);
  readonly startItem = signal(0);
  readonly endItem = signal(0);
  readonly appliedSearchTerm = signal('');
  readonly appliedSearchBy = signal('default');

  searchTypeOption = 'default';
  searchTypeText = '';

  get title(): string {
    const map: Record<AccountType, string> = {
        SHIP_TO: 'Ship-to Account',
        SOLD_TO: 'Sold-to Account',
    };
    return map[this.accountType] ?? 'Account';
  }

  get subtitle(): string {
    const map: Record<AccountType, string> = {
        SHIP_TO: 'Choose the account where this order should be shipped.',
        SOLD_TO: 'Choose the account this order should be sold to.',
    };
    return map[this.accountType] ?? 'Choose an account for this order.';
  }

  readonly pageTokens = computed<(number | '...')[]>(() => {
    const total = this.totalPages();
    const current = this.currentPage() + 1; // display is 1-based

    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 4) return [1, 2, 3, 4, '...', total];
    if (current >= total - 3) return [1, '...', total - 3, total - 2, total - 1, total];
    return [1, '...', current - 1, current, current + 1, '...', total];
  });

  ngOnChanges(changes: SimpleChanges): void {
    // Reused for both SHIP_TO and SOLD_TO: reload whenever the type or cart changes.
    if (!changes['accountType'] && !changes['cartId'] && !changes['initialData']) {
      return;
    }
    this.resetState();
    if (this.initialData?.accountsType === this.accountType) {
      this.applyResponse(this.initialData);
      return;
    }
    this.loadAccounts(0);
  }

  private resetState(): void {
    this.searchTypeOption = 'default';
    this.searchTypeText = '';
    this.appliedSearchTerm.set('');
    this.appliedSearchBy.set('default');
    this.accounts.set([]);
    this.currentPage.set(0);
    this.totalPages.set(0);
    this.totalResults.set(0);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.startItem.set(0);
    this.endItem.set(0);
  }

  loadAccounts(page: number): void {
    this.isLoading.set(true);
    const searchTerm = this.appliedSearchTerm();
    const searchBy = this.appliedSearchBy();
    const accountList$ = this.accountType === 'SOLD_TO'
      ? this.pagesService.searchSoldToAccounts(this.contextAccountUid, this.contextAccountName, page, searchTerm, searchBy)
      : this.pagesService.getAccountList(this.cartId, this.accountType, page, searchTerm, searchBy);
    accountList$.subscribe(response => {
      this.isLoading.set(false);
      this.applyResponse(response);
    });
  }

  private applyResponse(response: AccountListResponse | null): void {
    if (response?.allUnits) {
      this.accounts.set(response.allUnits.b2BUnits ?? []);
      const p = response.allUnits.paginationData;
      if (!p){
        this.currentPage.set(0);
        this.totalPages.set(1);
        this.totalResults.set(1);
        this.hasNext.set(false);
        this.hasPrevious.set(false);
      }else{
        this.currentPage.set(p.currentPage);
        this.totalPages.set(p.numberOfPages);
        this.totalResults.set(p.totalNumberOfResults);
        this.hasNext.set(p.hasNext);
        this.hasPrevious.set(p.hasPrevious);
        this.startItem.set(response.allUnits.startItem);
        this.endItem.set(response.allUnits.endItem);
      }
    } else {
      this.accounts.set([]);
      this.currentPage.set(0);
      this.totalPages.set(0);
      this.totalResults.set(0);
      this.hasNext.set(false);
      this.hasPrevious.set(false);
    }
  }

  onSearch(): void {
    this.appliedSearchTerm.set(this.searchTypeText.trim());
    this.appliedSearchBy.set(this.searchTypeOption);
    this.loadAccounts(0);
  }

  onClearSearch(): void {
    this.searchTypeText = '';
    this.appliedSearchTerm.set('');
    this.appliedSearchBy.set('default');
    this.searchTypeOption = 'default';
    this.loadAccounts(0);
  }

  onSelectAccount(unit: B2BUnit): void {
    this.accountSelected.emit(unit);
  }

  onGoToPage(page: number): void {
    this.loadAccounts(page);
  }

  onOverlayClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('account-select-overlay')) {
      this.closed.emit();
    }
  }

  onClose(): void {
    this.closed.emit();
  }
}
